import { useEffect, useRef } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import type { ImageThemeSetting } from '@jira/platform__project-theme-tokens/src/types.tsx';
import { generateMediaImageUrl } from '@jira/platform__generate-media-image-url/src/generateMediaImageUrl.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { MutationError } from '@jira/platform__utils-mutation-error/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	getDominantColor,
	SELECT_CUSTOM_IMAGE,
} from '@jira/platform__project-theme-components/src/common/getDominantColor.tsx';
import type { DominantColorPersister_project$key } from './__generated__/DominantColorPersister_project.graphql';
import type { DominantColorPersisterMutation } from './__generated__/DominantColorPersisterMutation.graphql';

const callWhenIdle = (callback: () => void): (() => void) => {
	if ('requestIdleCallback' in window && typeof requestIdleCallback === 'function') {
		const id = requestIdleCallback(callback);
		return () => cancelIdleCallback(id);
	}

	const id = setTimeout(callback, 0);
	return () => clearTimeout(id);
};

export const DominantColorPersister = ({
	themeSetting,
	media,
	projectFragment,
}: {
	themeSetting: ImageThemeSetting;
	media: { clientId: string; externalEndpointUrl: string };
	projectFragment: DominantColorPersister_project$key;
}): null => {
	const computedForRef = useRef<string | null>(null);
	const cloudId = useCloudId();

	const project = useFragment<DominantColorPersister_project$key>(
		graphql`
			fragment DominantColorPersister_project on JiraProject {
				projectId @required(action: THROW)
				canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) @required(action: THROW) {
					canPerform @required(action: THROW)
				}
			}
		`,
		projectFragment,
	);

	const [commitMutation] = useMutation<DominantColorPersisterMutation>(graphql`
		mutation DominantColorPersisterMutation(
			$entityId: ID!
			$backgroundValue: String!
			$backgroundType: JiraBackgroundType!
			$dominantColor: String
		) {
			jira_updateProjectBackground(
				input: {
					entityId: $entityId
					backgroundType: $backgroundType
					backgroundValue: $backgroundValue
					dominantColor: $dominantColor
				}
			) {
				success
				errors {
					message
				}
				project {
					background {
						...ThemeSetterBackground
					}
				}
			}
		}
	`);

	const onError = (error: Error) => {
		fireErrorAnalytics({
			meta: {
				id: 'persistDominantColor',
				packageName: 'jiraProjectThemeSetter',
				teamName: 'Wanjel',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	useEffect(() => {
		if (computedForRef.current === themeSetting.mediaApiFileId) {
			return;
		}

		let cancelled = false;
		const cancelIdle = callWhenIdle(() => {
			computedForRef.current = themeSetting.mediaApiFileId;

			const url = generateMediaImageUrl({
				mediaApiFileId: themeSetting.mediaApiFileId,
				mediaClientId: media.clientId,
				mediaExternalEndpointUrl: media.externalEndpointUrl,
				mediaEndpoint: 'image',
				token: themeSetting.mediaReadToken,
				width: 206,
			});

			getDominantColor(url, SELECT_CUSTOM_IMAGE)
				.then((color) => {
					if (cancelled) {
						return;
					}

					commitMutation({
						variables: {
							entityId: `ari:cloud:jira:${cloudId}:project/${project.projectId}`,
							backgroundType: 'CUSTOM',
							backgroundValue: themeSetting.id,
							dominantColor: color,
						},
						optimisticResponse: {
							jira_updateProjectBackground: {
								success: true,
								errors: null,
								project: {
									id: project.projectId,
									background: {
										__typename: 'JiraMediaBackground',
										customBackground: {
											id: themeSetting.id,
											dominantColor: color,
											mediaApiFileId: themeSetting.mediaApiFileId,
											mediaReadToken: themeSetting.mediaReadToken,
											sourceIdentifier: themeSetting.sourceIdentifier,
											sourceType: themeSetting.sourceType,
											altText: themeSetting.altText,
											brightness: themeSetting.brightness,
										},
									},
								},
							},
						},
						onCompleted: (response) => {
							const { errors } = response?.jira_updateProjectBackground ?? {};
							const error = errors?.find((e) => e.message);
							if (error) {
								onError(new MutationError(error));
							}
						},
						onError,
					});
				})
				.catch((error) => onError(error));
		});

		return () => {
			cancelled = true;
			cancelIdle();
		};
	}, [
		themeSetting,
		media,
		cloudId,
		commitMutation,
		project.projectId,
		project.canEditProjectConfig?.canPerform,
	]);

	return null;
};
